/**
 * Agents Management Page
 */
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { ChipListInput } from '@/components/common/ChipListInput';
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import UsageChip from '@/components/common/UsageChip';
import SearchBar from '@/components/ui/SearchBar';
import StatusChip from '@/components/ui/StatusChip';
import { usePlanGate } from '@/hooks/usePlanGate';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useAgentFiles } from '@/hooks/useAgentFiles';
import { useAgentQASources } from '@/hooks/useAgentQASources';
import { useAgentTextSources } from '@/hooks/useAgentTextSources';
import adminApi from '@/services/admin.api';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { isPermissionLockTagged } from '@/utils/permission-lock.util';
import {
  Add,
  Delete,
  DeleteOutline,
  EditOutlined,
  FilterList,
  InfoOutlined,
  PowerSettingsNewOutlined,
  SmartToy,
} from '@mui/icons-material';
import DeactivateAgentDialog, { USAGE_COPY, type AgentUsage } from '@/components/agents/DeactivateAgentDialog';
import DeleteAgentDialog from '@/components/agents/DeleteAgentDialog';
import { CHANNEL_TONE } from '@/components/agents/channelTones';
import {
  Alert,
  alpha,
  Box,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Skeleton,
  Switch,
  FormControlLabel,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { field, useUrlListState } from '@/hooks/admin/useUrlListState';

type AgentPurpose = 'chat' | 'lead' | 'appointment';

interface AgentTemplate {
  id: string;
  vertical: string;
  purpose: AgentPurpose;
  name: string;
  description: string;
  systemPrompt: string;
  topics?: string[];
  isGeneralFallback?: boolean;
}

// `effect` describes the intent of each goal. Note it deliberately does NOT say
// "collects nothing" for chat — "Data to collect" works independently of the
// goal, so any agent collects whatever is listed there.
const PURPOSE_META: Record<AgentPurpose, { label: string; color: any; effect: string }> = {
  chat: {
    label: 'General Chat',
    color: 'default',
    effect: 'Answers questions from your knowledge base. Best for support and FAQs.',
  },
  lead: {
    label: 'Lead Capture',
    color: 'primary',
    effect: 'Qualifies the visitor and captures their contact details.',
  },
  appointment: {
    label: 'Appointment',
    color: 'success',
    effect: 'Guides the visitor to a preferred date and time, then confirms it.',
  },
};

const PURPOSE_ORDER: AgentPurpose[] = ['chat', 'lead', 'appointment'];

/** Rows per request — matches the backend's own default page size. */
const PAGE_SIZE = 20;

/**
 * The actions the AI can take, mirroring the backend's TOOL_REGISTRY
 * (backend/src/services/ai-tools/tool-registry.ts). Kept as a flat const so
 * adding a tool later is one entry here, not new form JSX.
 *
 * `reply` is deliberately NOT selectable: it is how the AI answers at all, so
 * letting someone uncheck it would silently mute the agent rather than
 * restrict it. It is added back automatically when a restriction is saved
 * (see handleSubmit), so an allow-list can never exclude it.
 */
const AI_TOOL_OPTIONS: { key: string; label: string; description: string }[] = [
  { key: 'send_media', label: 'Send a file', description: 'Sends a document or image from your media library.' },
  {
    key: 'send_interactive_message',
    label: 'Send a list or button message',
    description:
      "Sends one of your saved WhatsApp list menus or reply-button messages. Only ones you've " +
      'marked "Let AI agents send this message" are ever available to pick from.',
  },
  { key: 'request_appointment', label: 'Request an appointment', description: 'Records a new booking request for your team to confirm.' },
  { key: 'reschedule_appointment', label: 'Reschedule an appointment', description: 'Moves an existing open request to a new time.' },
  { key: 'create_lead', label: 'Capture a lead', description: "Saves the customer's details and what they're interested in." },
  { key: 'update_contact_field', label: 'Update a contact field', description: 'Fills in one of your defined contact fields.' },
  { key: 'handoff_to_human', label: 'Hand off to a human', description: 'Escalates the conversation to a team member.' },
];

/** Always granted — see AI_TOOL_OPTIONS. */
const ALWAYS_ALLOWED_TOOL = 'reply';

/** Every switchable capability. Used as the ON-state for agents created before
 *  per-capability permissions existed (they run with every tool today, so the
 *  form must show that truthfully rather than as "nothing selected"). */
const ALL_TOOL_KEYS = AI_TOOL_OPTIONS.map((o) => o.key);

/**
 * The capabilities a template's purpose actually needs to do its job.
 *
 * Templates prefill a name, prompt and topics, so a "Appointment Booking
 * Assistant" whose agent cannot request an appointment is a template that
 * doesn't work. Since capabilities became explicit opt-in, that is exactly what
 * every appointment/lead template would produce without this — the prompt
 * promises a booking the agent has no way to record.
 *
 * This is a visible PREFILL, not a silent grant: the switches show the implied
 * capabilities in the create dialog before anything is saved, and the admin can
 * turn any of them off.
 */
export const PURPOSE_IMPLIED_TOOLS: Record<AgentPurpose, string[]> = {
  chat: [],
  lead: ['create_lead'],
  appointment: ['request_appointment', 'reschedule_appointment'],
};

/**
 * Union of what's already switched on with what the chosen template needs, in
 * AI_TOOL_OPTIONS order. A union rather than a replace: picking a template must
 * never silently revoke a capability the admin had already turned on.
 */
export function applyTemplateTools(purpose: AgentPurpose, current: string[]): string[] {
  const implied = PURPOSE_IMPLIED_TOOLS[purpose] ?? [];
  return ALL_TOOL_KEYS.filter((k) => current.includes(k) || implied.includes(k));
}

/**
 * Which switches to show for an agent's stored permissions.
 *
 * An absent array is a pre-permissions agent, which the backend still runs with
 * EVERY tool (`resolveActiveToolNames` reads undefined as "all") — so show all
 * switches on rather than implying the agent can do nothing. Saving then writes
 * that same set explicitly, which is how the implicit state drains out of the
 * data. `reply` is never shown: it is granted implicitly.
 */
export function toolsToFormState(storedTools: string[] | undefined): string[] {
  if (!Array.isArray(storedTools)) return [...ALL_TOOL_KEYS];
  return storedTools.filter((t) => t !== ALWAYS_ALLOWED_TOOL);
}

/**
 * The capability half of the agent save payload.
 *
 * Sent whenever AI Actions are available, and AUTHORITATIVE when sent: the
 * switches state the complete set. `reply` is re-added so an allow-list can
 * never mute the agent.
 *
 * Omitted entirely when the plan doesn't include AI Actions. The backend
 * shallow-MERGES `settings`, so an absent key preserves what is stored — which
 * is the whole point: saving an agent for an unrelated reason (a rename, a
 * prompt tweak) must never rewrite permissions the admin could not see or
 * change. Without this, a plan downgrade followed by any edit would silently
 * strip capabilities that should return on re-upgrade.
 */
export function buildToolsPayload(
  toolsLocked: boolean,
  allowedTools: string[]
): { tools?: string[] } {
  if (toolsLocked) return {};
  return { tools: [ALWAYS_ALLOWED_TOOL, ...allowedTools] };
}

/** Section heading with the long explanation tucked into a tooltip, so the form
 *  reads as fields rather than as documentation. */
function SectionLabel({ title, hint }: { title: string; hint: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      <Tooltip title={hint} arrow>
        <InfoOutlined sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }} />
      </Tooltip>
    </Box>
  );
}

/** Switch with a short label and a quiet second line, instead of a
 *  sentence-long label that wraps. */
function ToggleRow({
  checked,
  onChange,
  label,
  description,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
  disabled?: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, opacity: disabled ? 0.5 : 1 }}>
      <Switch
        size="small"
        checked={checked}
        disabled={Boolean(disabled)}
        onChange={(e) => onChange(e.target.checked)}
        sx={{ mt: 0.25 }}
      />
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Box>
  );
}

interface RoutingCandidateExplanation {
  agentId: string;
  agentName: string;
  matched: string[];
  isWinner: boolean;
  isGeneralFallback: boolean;
  eligibleForAutoRouting: boolean;
  topics: string[];
  routingKeywords: string[];
}

interface RoutingExplanation {
  tier: string;
  tierLabel: string;
  winnerAgentId: string | null;
  winnerAgentName: string | null;
  confidence: number;
  reason: string;
  candidates: RoutingCandidateExplanation[];
  emergencyFallback?: boolean;
}

interface Agent {
  _id: string;
  name: string;
  description?: string;
  purpose?: AgentPurpose;
  collectFields?: string[];
  systemPrompt: string;
  model?: string;
  llmModel?: string;
  topics?: string[];
  destinations?: string[];
  routingKeywords?: Array<{ keyword: string; weight: number }>;
  explicitKeywordsOnly?: boolean;
  isGeneralFallback?: boolean;
  eligibleForAutoRouting?: boolean;
  /** Per-channel auto-answer allow-list. Absent = all channels. */
  enabledChannels?: string[];
  isActive: boolean;
  knowledgeBase: {
    textContent?: string;
    sourceRefs?: {
      fileIds?: string[];
      textIds?: string[];
      qaIds?: string[];
    };
    sources?: {
      files?: any[];
      texts?: any[];
      qas?: any[];
    };
    counts?: {
      attachedFileCount?: number;
      attachedTextCount?: number;
      attachedQaCount?: number;
    };
  };
  settings?: {
    temperature?: number;
    maxTokens?: number;
    /** Allow-list of AI action names. Absent = every action is available
     *  (still subject to the plan's AI Tool-Calling feature). */
    tools?: string[];
  };
  metadata?: { usageCount?: number; lastUsedAt?: string; tags?: string[] };
  createdAt: string;
  updatedAt: string;
}

interface TextSourceOption {
  _id: string;
  title: string;
}

interface AgentKnowledgeCounts {
  files: number;
  texts: number;
  qas: number;
}

/** Topic values are tenant-defined, so that one stays a free string; the
 *  backend validates and an unknown value simply matches nothing. The in-dialog
 *  tab (Basic Info / Prompt / Knowledge / Settings) is deliberately NOT here —
 *  it is dialog state, not a view of the list. */
const AGENTS_URL_SPEC = {
  search: field.str(),
  topic: field.str('all'),
  purpose: field.str('all'),
  page: field.pageIndex(),
};

/** Row-action icon button: muted until hovered, then tinted by intent. */
const quietAction = (hover: string) => ({
  color: 'text.disabled',
  '& .MuiSvgIcon-root': { fontSize: 18 },
  '&:hover': { color: hover, backgroundColor: 'action.hover' },
});

type TagTone = 'keyword' | 'topic' | 'good' | 'info' | 'warn' | 'muted' | 'whatsapp' | 'instagram';
const TAG_TONES: Record<TagTone, { bg: string; fg: string }> = {
  keyword: { bg: '#eef2ff', fg: '#4338ca' },
  topic: { bg: '#f1f5f9', fg: '#475569' },
  good: { bg: '#dcfce7', fg: '#15803d' },
  info: { bg: '#dbeafe', fg: '#1d4ed8' },
  warn: { bg: '#fef3c7', fg: '#92400e' },
  muted: { bg: '#f1f5f9', fg: '#94a3b8' },
  // Channel colours — shared with the deactivate dialog (see channelTones.ts).
  whatsapp: CHANNEL_TONE.whatsapp,
  instagram: CHANNEL_TONE.instagram,
};

/** One tiny, consistent tag. Every chip in the table — keywords, topics, pins — goes through
 *  this so they share height, radius and the soft-tint palette instead of a mix of solid
 *  green / indigo / grey MUI chips. */
const Tag: React.FC<{
  label: string;
  tone: TagTone;
  dot?: boolean;
  title?: string;
  faded?: boolean;
  /** Outlined instead of filled: configured, but currently not answering. */
  dashed?: boolean;
}> = ({ label, tone, dot, title, faded, dashed }) => {
  const t = TAG_TONES[tone];
  const el = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.625,
        height: 22,
        px: 1,
        borderRadius: '6px',
        bgcolor: dashed ? 'transparent' : t.bg,
        color: t.fg,
        ...(dashed ? { border: `1px dashed ${t.fg}`, px: 0.875 } : {}),
        fontSize: '0.6875rem',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        opacity: faded ? 0.6 : 1,
      }}
    >
      {dot && <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.fg }} />}
      {label}
    </Box>
  );
  return title ? <Tooltip title={title}>{el}</Tooltip> : el;
};

const Agents: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  // Tenant-wide agent list, independent of the visible page. Only the routing
  // health banner and the topic filter's options read it — both describe the
  // whole tenant, so a fallback agent (or a topic) sitting on page 2 must still
  // count. The table itself is server-paginated below.
  const [allAgents, setAllAgents] = useState<Agent[]>([]);
  // Which agent each channel is pinned to (null = not pinned / not available on this plan).
  const [pins, setPins] = useState<{ whatsapp: string | null; instagram: string | null; webChat: string | null }>({
    whatsapp: null,
    instagram: null,
    webChat: null,
  });
  // A channel only counts as "this agent is used for it" when the tenant can actually use that
  // channel. Settings keep a pinned agent id even after a plan change / disconnect, so reading
  // the pin alone would list (and offer to re-assign) channels the tenant doesn't have.
  const { hasModuleAccess } = useModuleAccess();
  const whatsappAvailable = hasModuleAccess('whatsapp');
  const instagramAvailable = hasModuleAccess('instagram');
  const webChatAvailable = hasModuleAccess('leadForm');
  const [deleteTarget, setDeleteTarget] = useState<Agent | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<{ agent: Agent; fromEditForm: boolean } | null>(null);
  const [deactivateBusy, setDeactivateBusy] = useState(false);
  const [deactivateError, setDeactivateError] = useState('');
  const { state: query, patch: patchQuery } = useUrlListState(AGENTS_URL_SPEC);
  const page = query.page; // 0-indexed for TablePagination
  const setPage = (value: number) => patchQuery({ page: value });
  const [total, setTotal] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState(query.search);
  // Search is answered by the server (the list is paginated, so filtering the
  // 20 rows in hand would miss every match on another page). Debounced so
  // typing doesn't fire a request per keystroke.
  const debouncedSearch = query.search;
  const filterTopic = query.topic;
  const setFilterTopic = (value: string) => patchQuery({ topic: value });
  const filterPurpose = query.purpose as 'all' | AgentPurpose;
  const setFilterPurpose = (value: 'all' | AgentPurpose) => patchQuery({ purpose: value });
  const [groupByPurpose, setGroupByPurpose] = useState(false);
  const [routingTestMessage, setRoutingTestMessage] = useState('');
  const [routingTestResult, setRoutingTestResult] = useState<RoutingExplanation | null>(null);
  const [routingTestLoading, setRoutingTestLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agentKnowledgeCounts, setAgentKnowledgeCounts] = useState<
    Record<string, AgentKnowledgeCounts>
  >({});
  const [allTextSources, setAllTextSources] = useState<TextSourceOption[]>([]);
  const [loadingAllTextSources, setLoadingAllTextSources] = useState(false);
  const [selectedTextSourceId, setSelectedTextSourceId] = useState('');
  const [availableModels, setAvailableModels] = useState<Array<{ id: string; isEnabled: boolean }>>([]);

  // AI Actions are plan-gated. `hasFeature` reads the RESOLVED entitlement the
  // backend already sends (plan default ⊕ super-admin per-tenant override), so
  // this covers "not in the plan" and "explicitly disabled for this tenant"
  // alike. `guard` opens the global PlanLockDialog and never runs the handler.
  const { guard, hasFeature } = usePlanGate();
  const toolsLocked = !hasFeature('aiToolCalling');

  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [collectFieldOptions, setCollectFieldOptions] = useState<{ key: string; label: string }[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    purpose: 'chat' as AgentPurpose,
    collectFields: [] as string[],
    systemPrompt: '',
    model: '',
    topics: [] as string[],
    destinations: [] as string[],
    routingKeywords: [] as string[],
    explicitKeywordsOnly: false,
    isGeneralFallback: false,
    // Master on/off: an inactive agent is never selected on any channel.
    isActive: true,
    // Read-only carry-over of the stored "reserved" flag (no longer editable
    // here) so saving never changes it and the routing-signal check stays right.
    eligibleForAutoRouting: true,
    // Per-channel auto-answer, converted to/from the backend's `enabledChannels`
    // array at load/submit. Only WhatsApp is editable here; `answerInstagram` is
    // carried over unchanged (Instagram is chosen in Settings → Instagram).
    answerWhatsapp: true,
    answerInstagram: true,
    tags: [] as string[],
    /** The exact capabilities this agent may use, one entry per switch. Always
     *  sent explicitly on save — `reply` is added by the submit handler, so an
     *  empty list means "answers, but takes no actions". */
    allowedTools: [] as string[],
  });

  // Confirm dialogs
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detachFileContext, setDetachFileContext] = useState<{
    agentId: string;
    fileId: string;
    fileName: string;
  } | null>(null);
  const [detachQAContext, setDetachQAContext] = useState<{
    agentId: string;
    qaId: string;
    qaTitle: string;
  } | null>(null);
  const [detachTextContext, setDetachTextContext] = useState<{
    agentId: string;
    textId: string;
    textTitle: string;
  } | null>(null);

  const { files: attachedFiles, loading: loadingFiles, loadFiles, detachFile } = useAgentFiles();
  const { qas: attachedQAs, loading: loadingQAs, loadQAs, detachQA } = useAgentQASources();
  const {
    texts: attachedTextSources,
    loading: loadingTextSources,
    loadTexts,
    detachText,
  } = useAgentTextSources();

  useEffect(() => {
    // Page reset rides along with the debounced value (one render, one fetch);
    // resetting on keystroke would fetch page 1 with the OLD search first.
    // The page reset comes from the hook now — and only when the value really
    // moved, so a deep link to a later page survives the debounce firing on mount.
    const timer = setTimeout(() => patchQuery({ search: search.trim() }), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // One request per (page, search, topic, purpose). The backend answers all
  // four — search covers name/description/topics/routing keywords (see
  // agent.service.ts getAgents) — so a filter is applied to the whole tenant,
  // not to whichever page happened to be loaded. The filter handlers reset the
  // page to 0 themselves so a filter change and its page reset land in the
  // same render (one fetch, not two).
  useEffect(() => {
    fetchAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, filterTopic, filterPurpose]);

  // The banner and topic options need the whole tenant, not one page, so they
  // get their own read. Only refetched when the set can actually change
  // (create/update/delete) — never on paging or filtering.
  useEffect(() => {
    fetchAllAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappAvailable, instagramAvailable, webChatAvailable]);

  useEffect(() => {
    adminApi
      .getLLMConfigModels({ suppressPermissionLock: true })
      .then((models) => setAvailableModels(models || []))
      .catch(() => setAvailableModels([]));
  }, []);

  useEffect(() => {
    if (!agents.length) return;
    const loadKnowledgeCounts = async () => {
      const counts: Record<string, AgentKnowledgeCounts> = {};
      for (const agent of agents) {
        // The list payload already carries attached counts — no per-agent requests needed.
        if (agent.knowledgeBase?.counts) continue;
        try {
          const [files, texts, qas] = await Promise.all([
            adminApi.getAgentFiles(agent._id, { suppressPermissionLock: true }),
            adminApi.getAgentTextSources(agent._id, { suppressPermissionLock: true }),
            adminApi.getAgentQASources(agent._id, { suppressPermissionLock: true }),
          ]);
          counts[agent._id] = {
            files: files.length,
            texts: texts.length,
            qas: qas.length,
          };
        } catch {
          counts[agent._id] = { files: 0, texts: 0, qas: 0 };
        }
      }
      if (Object.keys(counts).length > 0) setAgentKnowledgeCounts((prev) => ({ ...prev, ...counts }));
    };
    loadKnowledgeCounts();
  }, [agents]);

  useEffect(() => {
    if (selectedAgent?._id && editMode && dialogOpen) {
      loadFiles(selectedAgent._id);
      loadQAs(selectedAgent._id);
      loadTexts(selectedAgent._id);
    }
  }, [selectedAgent?._id, editMode, dialogOpen]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const { data, meta } = await adminApi.getAgents({
        page: page + 1,
        limit: PAGE_SIZE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(filterTopic !== 'all' ? { topic: filterTopic } : {}),
        ...(filterPurpose !== 'all' ? { purpose: filterPurpose } : {}),
      });
      // adminApi.getAgents already runs normalizeAgentPayload internally —
      // same normalization this file used to duplicate locally.
      setAgents(data || []);
      setTotal(meta?.total ?? 0);
    } catch (err: any) {
      if (!isPermissionLockTagged(err)) {
        setError(err?.response?.data?.error?.message || err.message || 'Failed to fetch agents');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAllAgents = async () => {
    try {
      const { data } = await adminApi.getAgents({ limit: 500 }, { suppressPermissionLock: true });
      setAllAgents(data || []);
    } catch {
      setAllAgents([]);
    }
    // Each pin lives in its own settings blob (and behind its own plan module), so every read is
    // independent and a missing module just means "not pinned" — never an error dialog.
    const quiet = { suppressPermissionLock: true } as const;

    // Instagram needs three things to be "in use": the module on the plan, an actually connected
    // account, and Instagram AI switched on. Anything less and the pinned id is just stored config.
    const loadInstagramPin = async (): Promise<string | null> => {
      if (!instagramAvailable) return null;
      const [cfg, igSettings] = await Promise.all([
        adminApi.getInstagramConfig().catch(() => null),
        adminApi.getInstagramAgentSettings(quiet).catch(() => null),
      ]);
      return cfg?.isConnected && igSettings?.aiEnabled ? (igSettings.defaultAgentId ?? null) : null;
    };

    const [wa, igPin, lf] = await Promise.all([
      whatsappAvailable ? adminApi.getWhatsAppAgentSettings(quiet).catch(() => null) : Promise.resolve(null),
      loadInstagramPin(),
      webChatAvailable ? adminApi.getLeadFormSettings(quiet).catch(() => null) : Promise.resolve(null),
    ]);
    setPins({
      whatsapp: wa?.defaultAgentId ?? null,
      instagram: igPin,
      webChat: lf?.agentId ?? null,
    });
  };

  /** Refetch after a mutation, keeping the reader where they are. Deleting the
   *  last row of a page would otherwise strand them on an empty page. */
  const refreshAgents = (rowRemoved = false) => {
    if (rowRemoved && agents.length === 1 && page > 0) {
      setPage(page - 1); // the page effect refetches
    } else {
      fetchAgents();
    }
    fetchAllAgents();
  };

  /** Any filter change re-reads from the first page — a filter that only looked
   *  at page 4 would read as "no matches" for everything else. */
  const resetToFirstPage = () => setPage(0);

  const loadAllTextSources = async () => {
    try {
      setLoadingAllTextSources(true);
      const response = await adminApi.getTextSources({ limit: 500 }, { suppressPermissionLock: true });
      setAllTextSources(response.data || []);
    } catch {
      setAllTextSources([]);
    } finally {
      setLoadingAllTextSources(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const templates = await adminApi.getAgentTemplates({ suppressPermissionLock: true });
      setTemplates(templates);
    } catch {
      setTemplates([]);
    }
  };

  const loadCollectFields = async () => {
    const CORE = [
      { key: 'customerName', label: 'Name' },
      { key: 'customerPhone', label: 'Phone' },
      { key: 'customerEmail', label: 'Email' },
    ];
    try {
      const defs: any[] = await adminApi.getFieldDefinitions(undefined, { suppressPermissionLock: true });
      const leadDefs = (Array.isArray(defs) ? defs : [])
        .filter((d: any) => d.entity === 'lead')
        .map((d: any) => ({ key: d.key, label: d.label }));
      setCollectFieldOptions([...CORE, ...leadDefs]);
    } catch {
      setCollectFieldOptions(CORE);
    }
  };

  const applyTemplate = (t: AgentTemplate) => {
    setFormData((f) => ({
      ...f,
      name: f.name || t.name,
      description: t.description,
      purpose: t.purpose,
      systemPrompt: t.systemPrompt,
      topics: t.topics?.length ? t.topics : f.topics,
      isGeneralFallback: t.isGeneralFallback ?? f.isGeneralFallback,
      // A template's prompt promises an action; prefill the capability that
      // performs it, or the resulting agent cannot do what it was picked for.
      allowedTools: applyTemplateTools(t.purpose, f.allowedTools),
    }));
  };

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      purpose: 'chat',
      collectFields: [],
      systemPrompt: '',
      model: '',
      topics: [],
      destinations: [],
      routingKeywords: [],
      explicitKeywordsOnly: false,
      isGeneralFallback: false,
      isActive: true,
      eligibleForAutoRouting: true,
      // New agents answer every channel by default (enabledChannels absent).
      answerWhatsapp: true,
      answerInstagram: true,
      tags: [],
      // New agents start with no actions — each capability is an explicit
      // opt-in, matching the backend's DEFAULT_NEW_AGENT_TOOLS.
      allowedTools: [],
    });
    if (templates.length === 0) void fetchTemplates();
    if (collectFieldOptions.length === 0) void loadCollectFields();
    setEditMode(false);
    setSelectedAgent(null);
    setTab(0);
    setDialogOpen(true);
    setError('');
    setSuccess('');
    setSelectedTextSourceId('');
  };

  const handleOpenEdit = (agent: Agent) => {
    setFormData({
      name: agent.name,
      description: agent.description || '',
      purpose: agent.purpose || 'chat',
      collectFields: agent.collectFields || [],
      systemPrompt: agent.systemPrompt,
      model: agent.model || agent.llmModel || '',
      topics: agent.topics || [],
      destinations: agent.destinations || [],
      routingKeywords: (agent.routingKeywords || []).map((k) => k.keyword),
      explicitKeywordsOnly: agent.explicitKeywordsOnly || false,
      isGeneralFallback: agent.isGeneralFallback || false,
      isActive: agent.isActive !== false,
      eligibleForAutoRouting: true, // legacy "reserved" flag is retired; saving clears it
      // enabledChannels absent = all channels on. An explicit array means a
      // channel is answered only if it's listed.
      answerWhatsapp: !Array.isArray(agent.enabledChannels) || agent.enabledChannels.includes('whatsapp'),
      answerInstagram: !Array.isArray(agent.enabledChannels) || agent.enabledChannels.includes('instagram'),
      tags: agent.metadata?.tags || [],
      allowedTools: toolsToFormState(agent.settings?.tools),
    });
    setEditMode(true);
    setSelectedAgent(agent);
    setTab(0);
    setDialogOpen(true);
    setError('');
    setSuccess('');
    setSelectedTextSourceId('');
    loadAllTextSources();
    if (collectFieldOptions.length === 0) void loadCollectFields();
  };

  const handleAttachTextSource = async () => {
    if (!selectedAgent?._id || !selectedTextSourceId) return;

    try {
      setError('');
      await adminApi.attachTextSourcesToAgents([selectedTextSourceId], [selectedAgent._id]);
      await loadTexts(selectedAgent._id);
      setSelectedTextSourceId('');
      setSuccess('Text source attached');
    } catch (err: any) {
      setError(err.message || 'Failed to attach text source');
    }
  };

  const handleRoutingTest = async () => {
    const message = routingTestMessage.trim();
    if (!message) return;
    setRoutingTestLoading(true);
    setRoutingTestResult(null);
    try {
      const result = await adminApi.testAgentRouting(message);
      setRoutingTestResult(result as RoutingExplanation);
    } catch (err: any) {
      if (!isPermissionLockTagged(err)) {
        setError(err?.response?.data?.error?.message || 'Could not test routing');
      }
    } finally {
      setRoutingTestLoading(false);
    }
  };

  // Mirrors assertRoutingSignalPresent on the backend: an agent needs a topic
  // or a routing keyword unless it is the catch-all, or reserved (never in the
  // scoring pool, so it needs no way to be scored).
  const hasRoutingSignal =
    formData.isGeneralFallback ||
    !formData.eligibleForAutoRouting ||
    formData.topics.length > 0 ||
    formData.routingKeywords.length > 0;

  /** Has the author begun filling this form? Used to keep guidance quiet on a
   *  pristine dialog and only escalate once there's something to guide. */
  const formStarted = formData.name.trim().length > 0;

  const handleSubmit = async () => {
    try {
      setError('');
      setSuccess('');
      const payload = {
        name: formData.name,
        description: formData.description,
        purpose: formData.purpose,
        collectFields: formData.collectFields,
        systemPrompt: formData.systemPrompt,
        model: formData.model,
        topics: formData.topics,
        destinations: formData.destinations,
        // The UI edits keywords as plain strings, but weights are part of the
        // stored shape. Carry any existing weight through by keyword so
        // editing an agent here never silently flattens weights that were set
        // via the API; genuinely new keywords take the schema default of 10.
        routingKeywords: formData.routingKeywords.map((keyword) => ({
          keyword,
          weight:
            selectedAgent?.routingKeywords?.find(
              (k) => k.keyword.toLowerCase() === keyword.toLowerCase()
            )?.weight ?? 10,
        })),
        explicitKeywordsOnly: formData.explicitKeywordsOnly,
        isGeneralFallback: formData.isGeneralFallback,
        isActive: formData.isActive,
        // The legacy "reserved" flag is retired: Active replaces it, so every
        // save puts the agent back in default routing.
        eligibleForAutoRouting: true,
        // Per-channel auto-answer. Both on → send null to clear enabledChannels
        // back to "all channels" (the clean default, so the stored doc carries
        // no restriction). Otherwise send the explicit list of enabled channels
        // (possibly empty = answers no channel's default traffic).
        enabledChannels:
          formData.answerWhatsapp && formData.answerInstagram
            ? null
            : [
                ...(formData.answerWhatsapp ? ['whatsapp'] : []),
                ...(formData.answerInstagram ? ['instagram'] : []),
              ],
        settings: {
          // Temperature & max tokens are NOT sent per-agent any more — they live only
          // in the tenant's global LLM Config (Settings → LLM Configuration), the single
          // source of truth both the RAG and OpenRouter reply paths read. Agents that
          // still carry stale settings.temperature/maxTokens are ignored by the backend.
          // See buildToolsPayload — omitted while plan-locked so a save for an
          // unrelated reason can't rewrite permissions the admin couldn't see.
          // On CREATE while locked, omitting it lets the backend apply its
          // reply-only default (DEFAULT_NEW_AGENT_TOOLS).
          ...buildToolsPayload(toolsLocked, formData.allowedTools),
        },
        metadata: { tags: formData.tags },
      };
      if (editMode && selectedAgent?._id) {
        await adminApi.updateAgent(selectedAgent._id, payload);
      } else {
        await adminApi.createAgent(payload);
      }
      setSuccess(editMode ? 'Agent updated!' : 'Agent created!');
      setTimeout(() => {
        setDialogOpen(false);
        refreshAgents();
      }, 1000);
    } catch (err: any) {
      if (!showPlanLockIfTagged(err) && !isPermissionLockTagged(err)) {
        setError(err?.response?.data?.error?.message || err.message || 'Operation failed');
      }
    }
  };

  /** Everything this agent currently answers for, in the order the dialog lists it. */
  const usagesFor = (agent: Agent): AgentUsage[] => {
    const out: AgentUsage[] = [];
    if (agent.isGeneralFallback) out.push({ key: 'fallback', ...USAGE_COPY.fallback });
    if (pins.whatsapp === agent._id) out.push({ key: 'whatsapp', ...USAGE_COPY.whatsapp });
    if (pins.instagram === agent._id) out.push({ key: 'instagram', ...USAGE_COPY.instagram });
    if (pins.webChat === agent._id) out.push({ key: 'webChat', ...USAGE_COPY.webChat });
    return out;
  };

  const reloadAgentLists = async () => {
    await Promise.all([fetchAgents(), fetchAllAgents()]);
  };

  /** Deactivate flow: pin hand-overs first (any failure aborts before the agent is touched),
   *  then the agent itself — unless it was opened from the edit form, which saves with Update. */
  const confirmDeactivate = async (replacements: Partial<Record<AgentUsage['key'], string>>) => {
    if (!deactivateTarget) return;
    const { agent, fromEditForm } = deactivateTarget;
    setDeactivateBusy(true);
    setDeactivateError('');
    try {
      const usages = usagesFor(agent);
      const calls: Array<Promise<unknown>> = [];
      for (const u of usages) {
        const replacement = replacements[u.key] || '';
        if (u.key === 'whatsapp') calls.push(adminApi.updateWhatsAppAgentSettings(replacement || null));
        if (u.key === 'instagram') calls.push(adminApi.updateInstagramAgentSettings({ agentId: replacement || null }));
        if (u.key === 'webChat') calls.push(adminApi.updateLeadFormSettings({ agentId: replacement || null }));
      }
      await Promise.all(calls);

      const fallbackReplacement = usages.some((u) => u.key === 'fallback') ? replacements.fallback || '' : '';
      if (fallbackReplacement) {
        // Promoting another agent demotes this one server-side (radio-button semantics).
        await adminApi.updateAgent(fallbackReplacement, { isGeneralFallback: true });
      }

      if (fromEditForm) {
        setFormData((f) => ({ ...f, isActive: false, isGeneralFallback: false }));
      } else {
        // The server doesn't clear the fallback flag on deactivation, so say it explicitly.
        await adminApi.updateAgent(agent._id, { isActive: false, isGeneralFallback: false });
        setSuccess(`"${agent.name}" deactivated`);
      }
      setDeactivateTarget(null);
      await reloadAgentLists();
    } catch (err: any) {
      setDeactivateError(err?.response?.data?.error?.message || err.message || 'Could not deactivate the agent');
    } finally {
      setDeactivateBusy(false);
    }
  };

  const activateAgent = async (agent: Agent) => {
    try {
      await adminApi.updateAgent(agent._id, { isActive: true });
      setSuccess(`"${agent.name}" activated`);
      await reloadAgentLists();
    } catch (err: any) {
      if (!showPlanLockIfTagged(err) && !isPermissionLockTagged(err)) {
        setError(err?.response?.data?.error?.message || err.message || 'Could not activate the agent');
      }
    }
  };

  /** Optimistic delete: the row leaves the table the moment the admin confirms. The server now
   *  answers after the database work only (vector-index / file cleanup runs in the background),
   *  and on any failure the previous lists are restored. */
  const executeDeleteAgent = async (agent: Agent) => {
    const previousPage = agents;
    const previousAll = allAgents;
    setDeleteBusy(true);
    setAgents((prev) => prev.filter((a) => a._id !== agent._id));
    setAllAgents((prev) => prev.filter((a) => a._id !== agent._id));
    setDeleteTarget(null);
    try {
      await adminApi.deleteAgent(agent._id);
      setSuccess(`"${agent.name}" deleted`);
      refreshAgents(true);
    } catch (err: any) {
      setAgents(previousPage);
      setAllAgents(previousAll);
      if (!isPermissionLockTagged(err)) {
        setError(err?.response?.data?.error?.message || err.message || 'Failed to delete');
      }
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleConfirmAction = async () => {
    if (detachFileContext) {
      try {
        await detachFile(detachFileContext.agentId, detachFileContext.fileId, false);
        setSuccess('File removed from agent');
        await loadFiles(detachFileContext.agentId);
        refreshAgents();
      } catch (err: any) {
        setError(err.message || 'Failed');
      }
      setDetachFileContext(null);
    } else if (detachQAContext) {
      try {
        await detachQA(detachQAContext.agentId, detachQAContext.qaId);
        setSuccess('Q&A source removed');
        setDetachQAContext(null);
      } catch (err: any) {
        setError(err.message || 'Failed');
      }
    } else if (detachTextContext) {
      try {
        await detachText(detachTextContext.agentId, detachTextContext.textId);
        setSuccess('Text source removed');
        setDetachTextContext(null);
      } catch (err: any) {
        setError(err.message || 'Failed');
      }
    }
    setConfirmOpen(false);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  // Flattened union of every tag in use — an agent can carry several. Built
  // from the tenant-wide list so the dropdown doesn't shrink as you page.
  const topicOptions = Array.from(
    new Set(allAgents.flatMap((a) => a.topics || []).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));
  const availableTextSources = allTextSources.filter(
    (source) => !attachedTextSources.some((attached) => attached._id === source._id)
  );
  // `agents` is already the filtered page the server returned (search, topic
  // and purpose are all query params now), so re-filtering here would only
  // ever contradict the pager's count.
  const filteredAgents = agents;

  // Grouping is offered on `purpose` only. Topics are many-per-agent, so
  // grouping by them would render the same agent under several headings and
  // read as duplicates — they stay a filter facet instead.
  const agentGroups: Array<{ key: string; label: string; agents: Agent[] }> = groupByPurpose
    ? PURPOSE_ORDER.map((p) => ({
        key: p,
        label: PURPOSE_META[p].label,
        agents: filteredAgents.filter((a) => (a.purpose || 'chat') === p),
      })).filter((g) => g.agents.length > 0)
    : [{ key: 'all', label: '', agents: filteredAgents }];

  // Tenant-level health, computed over the ELIGIBLE pool — a reserved agent
  // isn't a candidate for default routing at all, so it shouldn't count toward
  // "do we have enough agents / a fallback" any more than an inactive one does.
  // Reads the tenant-wide list, never the current page: "who answers by
  // default" is not a property of the twenty rows you happen to be looking at.
  const activeAgents = allAgents.filter((a) => a.isActive);
  const eligibleActiveAgents = activeAgents.filter((a) => a.eligibleForAutoRouting !== false);
  const fallbackAgent = eligibleActiveAgents.find((a) => a.isGeneralFallback);
  // Severe: every active agent is reserved, so WhatsApp has nothing to answer
  // with except an emergency pick — distinct from just "no fallback chosen".
  const showAllReservedWarning = activeAgents.length > 0 && eligibleActiveAgents.length === 0;

  const isUnroutable = (agent: Agent) =>
    agent.eligibleForAutoRouting !== false &&
    !agent.isGeneralFallback &&
    (agent.topics || []).length === 0 &&
    (agent.routingKeywords || []).length === 0;

  return (
    <Box>
      <PageHeader
        title="AI Agents"
        subtitle="Create and manage your AI agent configurations"
        actions={
          <>
            <UsageChip resource="agents" label="Agents" />
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
              New Agent
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Health banner: only when something actually needs attention. "Default responder: X" used
          to be shown permanently, which is just noise once a default is set — the table already
          tags that agent. */}
      {activeAgents.length > 0 && (showAllReservedWarning || (!fallbackAgent && eligibleActiveAgents.length > 1)) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {showAllReservedWarning ? (
            <Typography variant="body2">
              Every active agent is reserved — none will answer default WhatsApp messages. One will still
              respond as an emergency fallback, but mark at least one agent as eligible to fix this properly.
            </Typography>
          ) : (
            <Typography variant="body2">
              No default responder set — an unmatched message will go to an unpredictable one of your{' '}
              {eligibleActiveAgents.length} eligible agents. Set a general fallback agent, or use Test routing
              below to see the current pick.
            </Typography>
          )}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 2.5 }}>
        <CardContent sx={{ py: '14px !important' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search name, topic, keyword…"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                size="small"
                value={filterPurpose}
                onChange={(e) => {
                  setFilterPurpose(e.target.value as 'all' | AgentPurpose);
                  resetToFirstPage();
                }}
              >
                <MenuItem value="all">All goals</MenuItem>
                {PURPOSE_ORDER.map((p) => (
                  <MenuItem key={p} value={p}>
                    {PURPOSE_META[p].label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                select
                size="small"
                value={filterTopic}
                onChange={(e) => {
                  setFilterTopic(e.target.value);
                  resetToFirstPage();
                }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, color: 'text.disabled', display: 'flex' }}>
                      <FilterList sx={{ fontSize: 18 }} />
                    </Box>
                  ),
                }}
              >
                <MenuItem value="all">All topics</MenuItem>
                {topicOptions.map((topic) => (
                  <MenuItem key={topic} value={topic}>
                    {topic}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={groupByPurpose}
                    onChange={(e) => setGroupByPurpose(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Group by goal</Typography>}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Routing tester — routing is otherwise invisible config; this shows an
          author what actually happens to a given message. */}
      <Card sx={{ mb: 2.5, borderColor: '#e8ecf3' }}>
        <CardContent sx={{ py: '16px !important' }}>
          <SectionLabel
            title="Test routing"
            hint="Runs the real router against a sample message. Nothing is sent and no conversation is created."
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. do you have honeymoon packages?"
              value={routingTestMessage}
              onChange={(e) => setRoutingTestMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleRoutingTest();
              }}
            />
            <Button
              variant="contained"
              onClick={handleRoutingTest}
              disabled={!routingTestMessage.trim() || routingTestLoading}
              sx={{ minWidth: 88 }}
            >
              {routingTestLoading ? <CircularProgress size={20} color="inherit" /> : 'Test'}
            </Button>
          </Box>

          {routingTestResult && (() => {
            const tone: 'good' | 'info' | 'warn' = routingTestResult.emergencyFallback
              ? 'warn'
              : routingTestResult.tier === 'first-active'
                ? 'warn'
                : routingTestResult.tier === 'fallback' || routingTestResult.tier === 'legacy'
                  ? 'info'
                  : 'good';
            const palette = TAG_TONES[tone];
            return (
              <Box sx={{ mt: 2 }}>
                {routingTestResult.winnerAgentId ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: palette.bg,
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#fff',
                        color: palette.fg,
                      }}
                    >
                      <SmartToy fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: palette.fg }}>
                        <strong>{routingTestResult.winnerAgentName}</strong> would answer
                      </Typography>
                      <Typography variant="caption" sx={{ color: palette.fg, opacity: 0.85, display: 'block' }}>
                        Matched on {routingTestResult.tierLabel} · {routingTestResult.reason}
                      </Typography>
                      {routingTestResult.emergencyFallback && (
                        <Typography variant="caption" sx={{ color: palette.fg, fontWeight: 700 }}>
                          Every agent is reserved — this is an emergency pick, not a normal match.
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: '#fff',
                        color: palette.fg,
                        textAlign: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.1 }}>
                        {routingTestResult.confidence}
                      </Typography>
                      <Typography sx={{ fontSize: '0.625rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        confidence
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="warning">{routingTestResult.reason}</Alert>
                )}

                <Box sx={{ mt: 1.5 }}>
                  {routingTestResult.candidates.map((c, i) => (
                    <Box
                      key={c.agentId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                        px: 0.5,
                        borderTop: i === 0 ? 'none' : '1px solid #eef1f6',
                      }}
                    >
                      <Box sx={{ width: 200, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: c.isWinner ? 700 : 500 }}>
                          {c.agentName}
                        </Typography>
                        {!c.eligibleForAutoRouting && <Tag label="Reserved" tone="muted" />}
                        {c.isGeneralFallback && <Tag label="Fallback" tone="warn" />}
                      </Box>
                      <Box sx={{ flex: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        {!c.eligibleForAutoRouting ? (
                          <Typography variant="caption" color="text.disabled">
                            Reserved — excluded from auto-routing
                          </Typography>
                        ) : c.matched.length > 0 ? (
                          c.matched.map((m) => <Tag key={m} label={m} tone="good" />)
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            nothing matched
                          </Typography>
                        )}
                      </Box>
                      {c.isWinner && <Tag label="Winner" tone="keyword" />}
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent sx={{ p: '0 !important' }}>
          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton height={16} width="40%" />
                    <Skeleton height={14} width="70%" />
                  </Box>
                  <Skeleton width={80} height={24} />
                  <Skeleton width={80} height={24} />
                  <Skeleton width={80} height={32} />
                </Box>
              ))}
            </Box>
          ) : filteredAgents.length === 0 ? (
            <EmptyState
              icon={<SmartToy />}
              title={search ? 'No agents match your search' : 'No agents yet'}
              description={
                search ? 'Try a different keyword' : 'Create your first AI agent to get started'
              }
              {...(!search && {
                action: { label: 'Create Agent', onClick: handleOpenCreate, startIcon: <Add /> },
              })}
            />
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Agent</TableCell>
                    <TableCell>Goal</TableCell>
                    <TableCell>Triggers</TableCell>
                    <TableCell>Knowledge</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right" sx={{ width: 132 }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentGroups.map((group) => (
                    <React.Fragment key={group.key}>
                      {groupByPurpose && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ bgcolor: 'action.hover', py: 0.75, borderBottom: 0 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {group.label} · {group.agents.length}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {group.agents.map((agent) => {
                        const kb = agent.knowledgeBase?.counts;
                        const counts =
                          agentKnowledgeCounts[agent._id] ??
                          (kb
                            ? {
                                files: kb.attachedFileCount ?? 0,
                                texts: kb.attachedTextCount ?? 0,
                                qas: kb.attachedQaCount ?? 0,
                              }
                            : undefined);
                        const isReserved = agent.eligibleForAutoRouting === false;
                        const triggers: Array<{ label: string; tone: TagTone; title: string }> = [
                          ...(agent.routingKeywords || []).map((k) => ({
                            label: k.keyword,
                            tone: 'keyword' as TagTone,
                            title: isReserved
                              ? 'Routing keyword — inactive while this agent is reserved'
                              : 'Routing keyword — checked first',
                          })),
                          ...(agent.topics || []).map((topic) => ({
                            label: topic,
                            tone: 'topic' as TagTone,
                            title: isReserved ? 'Topic — inactive while this agent is reserved' : 'Topic',
                          })),
                        ];
                        const MAX_TRIGGERS = 3;
                        const shown = triggers.slice(0, MAX_TRIGGERS);
                        const hidden = triggers.slice(MAX_TRIGGERS);
                        const pinTags: Array<{ label: string; tone: TagTone; title: string; dashed?: boolean }> = [];
                        const answersWhatsapp =
                          agent.isActive &&
                          !isReserved &&
                          (!Array.isArray(agent.enabledChannels) || agent.enabledChannels.includes('whatsapp'));
                        if (agent.isGeneralFallback && agent.isActive) {
                          pinTags.push({ label: 'Default fallback', tone: 'info', title: 'Answers anything no other agent matches' });
                        }
                        // WhatsApp — always say where this agent stands, green when it is answering.
                        if (pins.whatsapp === agent._id) {
                          pinTags.push(
                            agent.isActive
                              ? { label: 'WhatsApp default', tone: 'whatsapp', title: 'Pinned in Settings — answers every WhatsApp message' }
                              : { label: 'WhatsApp pin inactive', tone: 'whatsapp', dashed: true, title: 'Pinned to WhatsApp but inactive — it is NOT answering' }
                          );
                        } else if (answersWhatsapp) {
                          pinTags.push({
                            label: 'WhatsApp',
                            tone: 'whatsapp',
                            title: 'In the routing pool — answers inbound WhatsApp messages that match its keywords/topics, or via the fallback tiers',
                          });
                        } else if (agent.isActive && !isReserved) {
                          pinTags.push({
                            label: 'WhatsApp off',
                            tone: 'muted',
                            title: 'WhatsApp answering is turned off for this agent (Edit → Settings)',
                          });
                        }
                        if (pins.instagram === agent._id) {
                          pinTags.push(
                            agent.isActive
                              ? { label: 'Instagram', tone: 'instagram', title: 'Pinned in Settings → Instagram — answers every Instagram DM' }
                              : { label: 'Instagram pin inactive', tone: 'instagram', dashed: true, title: 'Pinned to Instagram but inactive — Instagram AI is NOT replying' }
                          );
                        }
                        if (pins.webChat === agent._id) {
                          pinTags.push(
                            agent.isActive
                              ? { label: 'Web chat', tone: 'info', title: 'Powers your public web chat' }
                              : { label: 'Web chat pin inactive', tone: 'info', dashed: true, title: 'Pinned to web chat but inactive — it is NOT answering' }
                          );
                        }
                        if (isReserved) {
                          pinTags.push({ label: 'Reserved', tone: 'muted', title: 'Excluded from automatic routing' });
                        }
                        return (
                          <TableRow key={agent._id} hover>
                            <TableCell sx={{ py: 1.75 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                <Box
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 1.5,
                                    flexShrink: 0,
                                    bgcolor: alpha('#4f46e5', 0.1),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    '& svg': { fontSize: 18, color: '#4f46e5' },
                                  }}
                                >
                                  <SmartToy />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={600}>
                                    {agent.name}
                                  </Typography>
                                  {agent.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: 'block',
                                        maxWidth: 260,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {agent.description}
                                    </Typography>
                                  )}
                                  {pinTags.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                                      {pinTags.map((p) => (
                                        <Tag
                                          key={p.label}
                                          label={p.label}
                                          tone={p.tone}
                                          title={p.title}
                                          dot
                                          {...(p.dashed ? { dashed: true } : {})}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              <Tooltip title={PURPOSE_META[agent.purpose || 'chat'].effect}>
                                <Chip
                                  label={PURPOSE_META[agent.purpose || 'chat'].label}
                                  size="small"
                                  color={PURPOSE_META[agent.purpose || 'chat'].color}
                                  variant="outlined"
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                {shown.map((t) => (
                                  <Tag key={`${t.tone}:${t.label}`} label={t.label} tone={t.tone} title={t.title} faded={isReserved} />
                                ))}
                                {hidden.length > 0 && (
                                  <Tooltip title={hidden.map((h) => h.label).join(', ')}>
                                    <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default', px: 0.5 }}>
                                      +{hidden.length}
                                    </Typography>
                                  </Tooltip>
                                )}
                                {triggers.length === 0 &&
                                  (isUnroutable(agent) ? (
                                    <Tooltip title="No keywords or topics yet — add one so messages can reach this agent">
                                      <Typography variant="caption" color="text.disabled">
                                        Not set up
                                      </Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">
                                      —
                                    </Typography>
                                  ))}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              {counts ? (
                                counts.files + counts.texts + counts.qas === 0 ? (
                                  <Typography variant="caption" color="text.disabled">
                                    No knowledge yet
                                  </Typography>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">
                                    {counts.files} file{counts.files !== 1 ? 's' : ''} · {counts.texts} text · {counts.qas} Q&amp;A
                                  </Typography>
                                )
                              ) : (
                                <Skeleton width={90} height={16} />
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.75 }}>
                              <StatusChip status={agent.isActive ? 'active' : 'inactive'} dot />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.75, whiteSpace: 'nowrap' }}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(agent)}
                                  aria-label="Edit agent"
                                  sx={quietAction('#4f46e5')}
                                >
                                  <EditOutlined />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={agent.isActive ? 'Deactivate' : 'Activate'}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (agent.isActive) {
                                      setDeactivateError('');
                                      setDeactivateTarget({ agent, fromEditForm: false });
                                    } else {
                                      void activateAgent(agent);
                                    }
                                  }}
                                  aria-label={agent.isActive ? 'Deactivate agent' : 'Activate agent'}
                                  sx={quietAction(agent.isActive ? '#d97706' : '#16a34a')}
                                >
                                  <PowerSettingsNewOutlined />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteTarget(agent)}
                                  aria-label="Delete agent"
                                  sx={quietAction('#dc2626')}
                                >
                                  <DeleteOutline />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {/* Outside the empty/table branch on purpose: a client-side filter
              that matches nothing on this page still needs a way back to the
              other pages. */}
          {!loading && total > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
            />
          )}
        </CardContent>
      </Card>

      {/* Agent Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Edit Agent' : 'Create Agent'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ mb: 2.5, borderBottom: '1px solid #e2e8f0' }}
          >
            <Tab label="Basic Info" />
            <Tab label="System Prompt" />
            <Tab label="Knowledge Base" />
            <Tab label="Settings" />
          </Tabs>

          {tab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {!editMode && templates.length > 0 && (
                <TextField
                  fullWidth
                  select
                  label="Start from a template"
                  value=""
                  onChange={(e) => {
                    const t = templates.find((x) => x.id === e.target.value);
                    if (t) applyTemplate(t);
                  }}
                >
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.vertical !== 'generic' ? `[${t.vertical}] ` : ''}
                      {t.name} · {PURPOSE_META[t.purpose].label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                fullWidth
                label="Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <TextField
                fullWidth
                label="Description"
                placeholder="Optional — a short note for your team"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
              {/* A dropdown that arrives pre-answered gets skipped without being
                  read. Laying the goals out side by side makes it a decision. */}
              <Box>
                <SectionLabel
                  title="Conversation goal"
                  hint="What this agent does once it's answering. It does not decide which agent gets picked — routing keywords and topics do that."
                />
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={formData.purpose}
                  onChange={(_, v) => v && setFormData({ ...formData, purpose: v as AgentPurpose })}
                  sx={{ mt: 1 }}
                >
                  {PURPOSE_ORDER.map((p) => (
                    <ToggleButton key={p} value={p} sx={{ textTransform: 'none' }}>
                      {PURPOSE_META[p].label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.75 }}
                >
                  {PURPOSE_META[formData.purpose].effect}
                </Typography>
              </Box>
              <Autocomplete
                multiple
                size="small"
                options={collectFieldOptions}
                getOptionLabel={(o) => (o as { label: string }).label}
                isOptionEqualToValue={(o, v) => (o as any).key === (v as any).key}
                value={collectFieldOptions.filter((o) => formData.collectFields.includes(o.key))}
                onChange={(_, v) =>
                  setFormData({ ...formData, collectFields: (v as { key: string }[]).map((o) => o.key) })
                }
                renderInput={(params) => (
                  <TextField
                    {...(params as any)}
                    label="Data to collect"
                    placeholder="Name, Phone, Budget…"
                    helperText="Leave empty to collect all your defined lead fields."
                  />
                )}
              />
              <TextField
                fullWidth
                select
                label="Model"
                value={formData.model}
                onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                // An empty value means "use the system default". Without displayEmpty (+ a shrunk
                // label) MUI renders that selection as a blank field, which reads as "nothing chosen".
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  renderValue: (v) =>
                    v ? (
                      String(v)
                    ) : (
                      <Box component="em" sx={{ color: 'text.secondary' }}>
                        System default
                      </Box>
                    ),
                }}
                helperText={
                  availableModels.filter((m) => m.isEnabled !== false).length === 0
                    ? 'No models added yet — add one in Settings → LLM Config'
                    : undefined
                }
              >
                <MenuItem value="">
                  <em>System default</em>
                </MenuItem>
                {availableModels
                  .filter((m) => m.isEnabled !== false)
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>{m.id}</MenuItem>
                  ))}
              </TextField>
              {formData.destinations.length > 0 && (
                <TextField
                  fullWidth
                  label="Destinations"
                  value={formData.destinations.join(', ')}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      destinations: e.target.value.split(',').map((d) => d.trim()),
                    })
                  }
                  placeholder="e.g., Uzbekistan, Kazakhstan"
                />
              )}
              <Divider sx={{ mt: 1 }} />

              <Box>
                <SectionLabel
                  title="Routing"
                  hint="Incoming messages are matched against routing keywords first, then topics. The first match wins. Anything that matches neither goes to the general fallback agent."
                />
                <Typography variant="caption" color="text.secondary">
                  When should this agent answer?
                </Typography>
              </Box>

              <ChipListInput
                label="Routing keywords"
                placeholder="Exact phrases customers send…"
                values={formData.routingKeywords}
                onChange={(next) => setFormData({ ...formData, routingKeywords: next })}
                maxLength={200}
              />

              <ChipListInput
                label="Topics"
                placeholder="visa, honeymoon packages, refund policy…"
                values={formData.topics}
                onChange={(next) => setFormData({ ...formData, topics: next })}
                maxLength={50}
              />

              {/* Quiet until the author has actually started — a pristine form
                  scolding you for not filling in a field you haven't reached
                  yet is noise, not guidance. */}
              {!hasRoutingSignal && (
                <Typography
                  variant="caption"
                  color={formStarted ? 'warning.main' : 'text.secondary'}
                >
                  Add a keyword or topic so messages can reach this agent — or make it
                  the fallback, or reserve it, below.
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <ToggleRow
                  checked={formData.isActive}
                  onChange={(v) => {
                    // Turning OFF an agent that is live: show what it covers first.
                    if (!v && editMode && selectedAgent?.isActive) {
                      setDeactivateError('');
                      setDeactivateTarget({ agent: selectedAgent, fromEditForm: true });
                      return;
                    }
                    setFormData({
                      ...formData,
                      isActive: v,
                      // An inactive agent can never be the fallback; clear it so
                      // the saved state is never contradictory.
                      isGeneralFallback: v ? formData.isGeneralFallback : false,
                    });
                  }}
                  label="Active agent"
                  description={
                    formData.isActive
                      ? 'On — this agent can answer.'
                      : 'Off — this agent answers nothing on any channel (WhatsApp, Instagram, workflows, pins).'
                  }
                />
                <ToggleRow
                  checked={formData.answerWhatsapp}
                  onChange={(v) => setFormData({ ...formData, answerWhatsapp: v })}
                  disabled={!formData.isActive}
                  label="Answer WhatsApp messages"
                  description={
                    !formData.isActive
                      ? 'Activate the agent to enable this.'
                      : formData.answerWhatsapp
                          ? 'This agent can be auto-selected to answer inbound WhatsApp chats.'
                          : 'Off — skipped for WhatsApp auto-routing (still reachable via a WhatsApp pin/workflow).'
                  }
                />
                <ToggleRow
                  checked={formData.isGeneralFallback}
                  onChange={(v) => setFormData({ ...formData, isGeneralFallback: v })}
                  disabled={!formData.isActive}
                  label="General fallback agent"
                  description={
                    !formData.isActive
                      ? 'Activate the agent to enable this.'
                      : formData.isGeneralFallback
                          ? 'Answers anything no other agent matches. Only one agent can hold this — turning it on here removes it from the current one.'
                          : 'Answers anything no other agent matches.'
                  }
                />
                {/* Meaningless with no keywords — and the backend rejects it —
                    so prevent the state instead of erroring on save. */}
                <ToggleRow
                  checked={formData.explicitKeywordsOnly}
                  onChange={(v) => setFormData({ ...formData, explicitKeywordsOnly: v })}
                  disabled={formData.routingKeywords.length === 0}
                  label="Match keywords only"
                  description={
                    formData.routingKeywords.length === 0
                      ? 'Add a routing keyword to enable this.'
                      : "Ignore topics and this agent's own wording."
                  }
                />
              </Box>
            </Box>
          )}

          {tab === 1 && (
            <TextField
              fullWidth
              label="System Prompt"
              value={formData.systemPrompt}
              onChange={(e) => setFormData({ ...formData, systemPrompt: e.target.value })}
              multiline
              rows={16}
              required
              placeholder="Enter the system prompt / instructions for this agent…"
              sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
            />
          )}

          {tab === 2 && (
            <Box>
              {editMode && selectedAgent ? (
                <>
                  {/* Files */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Attached Files
                  </Typography>
                  {loadingFiles ? (
                    <CircularProgress size={20} />
                  ) : attachedFiles.length > 0 ? (
                    <List dense disablePadding sx={{ mb: 2 }}>
                      {attachedFiles.map((file) => (
                        <ListItem key={file._id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={file.name}
                            secondary={`${formatFileSize(file.size)} · ${new Date(file.createdAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => {
                                setDetachFileContext({
                                  agentId: selectedAgent._id,
                                  fileId: file._id,
                                  fileName: file.name,
                                });
                                setConfirmOpen(true);
                              }}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No files attached.
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Q&A Sources
                  </Typography>
                  {loadingQAs ? (
                    <CircularProgress size={20} />
                  ) : attachedQAs.length > 0 ? (
                    <List dense disablePadding sx={{ mb: 2 }}>
                      {attachedQAs.map((qa) => (
                        <ListItem key={qa._id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={qa.title}
                            secondary={`${qa.questions.length} question${qa.questions.length !== 1 ? 's' : ''}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => {
                                setDetachQAContext({
                                  agentId: selectedAgent._id,
                                  qaId: qa._id,
                                  qaTitle: qa.title,
                                });
                                setConfirmOpen(true);
                              }}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No Q&A sources attached.
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Text Sources
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Add Text Source"
                      value={selectedTextSourceId}
                      onChange={(e) => setSelectedTextSourceId(e.target.value)}
                      disabled={loadingAllTextSources || !selectedAgent?._id}
                    >
                      {availableTextSources.length === 0 ? (
                        <MenuItem value="" disabled>
                          {loadingAllTextSources
                            ? 'Loading text sources...'
                            : 'No unlinked text sources available'}
                        </MenuItem>
                      ) : (
                        availableTextSources.map((source) => (
                          <MenuItem key={source._id} value={source._id}>
                            {source.title}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                    <Button
                      variant="outlined"
                      onClick={handleAttachTextSource}
                      disabled={!selectedTextSourceId || loadingAllTextSources}
                    >
                      Attach
                    </Button>
                  </Box>

                  {loadingTextSources ? (
                    <CircularProgress size={20} />
                  ) : attachedTextSources.length > 0 ? (
                    <List dense disablePadding sx={{ mb: 2 }}>
                      {attachedTextSources.map((text) => (
                        <ListItem key={text._id} sx={{ px: 0 }}>
                          <ListItemText
                            primary={text.title}
                            secondary={`Updated ${new Date(text.updatedAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              color="error"
                              onClick={() => {
                                setDetachTextContext({
                                  agentId: selectedAgent._id,
                                  textId: text._id,
                                  textTitle: text.title,
                                });
                                setConfirmOpen(true);
                              }}
                            >
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      No text sources attached.
                    </Typography>
                  )}

                </>
              ) : (
                <Alert severity="info">
                  Save the agent first, then you can attach knowledge base files and text.
                </Alert>
              )}
            </Box>
          )}

          {tab === 3 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <SectionLabel
                  title="Actions"
                  hint="Beyond replying, this agent can take actions mid-conversation — sending a file, recording an appointment request, capturing a lead. Actions only run if your plan includes AI Tool-Calling and the agent's model supports it."
                />
                {toolsLocked ? (
                  <Typography variant="caption" color="text.secondary">
                    AI Actions aren&apos;t included on your current plan. These stay
                    exactly as they are until it&apos;s enabled — nothing is changed
                    or lost.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Turn on only what this agent should be able to do. Replying is
                    always allowed and is not listed.
                  </Typography>
                )}
                {/* When locked the whole block is one big click target that opens
                    the upgrade dialog. The switches inside are both `disabled`
                    AND pointer-events:none — a disabled input fires no click of
                    its own, so without that the rows would swallow the press and
                    the dialog would only open on the gaps between them. */}
                <Box
                  {...(toolsLocked
                    ? {
                        onClick: guard('aiToolCalling', () => {}),
                        sx: {
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          cursor: 'pointer',
                          '& .MuiSwitch-root': { pointerEvents: 'none' },
                        },
                      }
                    : { sx: { display: 'flex', flexDirection: 'column', gap: 1.5 } })}
                >
                  {AI_TOOL_OPTIONS.map((option) => (
                    <ToggleRow
                      key={option.key}
                      checked={formData.allowedTools.includes(option.key)}
                      disabled={toolsLocked}
                      onChange={(v) =>
                        setFormData({
                          ...formData,
                          // Keep AI_TOOL_OPTIONS order rather than click order, so the
                          // stored list is stable and diffable across saves.
                          allowedTools: v
                            ? ALL_TOOL_KEYS.filter(
                                (k) => k === option.key || formData.allowedTools.includes(k)
                              )
                            : formData.allowedTools.filter((k) => k !== option.key),
                        })
                      }
                      label={option.label}
                      description={option.description}
                    />
                  ))}
                </Box>
                {!toolsLocked && formData.allowedTools.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    No actions enabled — this agent will reply, but take no actions.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !formData.name ||
              !formData.systemPrompt ||
              // Only gate creation. Legacy agents predate this rule and must
              // stay editable — the backend applies the same carve-out.
              (!editMode && !hasRoutingSignal)
            }
          >
            {editMode ? 'Update Agent' : 'Create Agent'}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteAgentDialog
        open={Boolean(deleteTarget)}
        agentName={deleteTarget?.name ?? ''}
        isActive={Boolean(deleteTarget?.isActive)}
        busy={deleteBusy}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && void executeDeleteAgent(deleteTarget)}
        onDeactivateInstead={() => {
          const target = deleteTarget;
          setDeleteTarget(null);
          if (target) {
            setDeactivateError('');
            setDeactivateTarget({ agent: target, fromEditForm: false });
          }
        }}
      />

      <DeactivateAgentDialog
        open={Boolean(deactivateTarget)}
        agentName={deactivateTarget?.agent.name ?? ''}
        usages={deactivateTarget ? usagesFor(deactivateTarget.agent) : []}
        candidates={allAgents
          .filter((a) => a.isActive && a._id !== deactivateTarget?.agent._id)
          .map((a) => ({ id: a._id, name: a.name }))}
        busy={deactivateBusy}
        error={deactivateError}
        fromEditForm={Boolean(deactivateTarget?.fromEditForm)}
        onCancel={() => setDeactivateTarget(null)}
        onConfirm={(replacements) => void confirmDeactivate(replacements)}
      />

      {/* Confirm (detach flows) */}
      <ConfirmDialog
        open={confirmOpen}
        title={
          detachFileContext
            ? 'Detach File'
            : detachQAContext
              ? 'Detach Q&A Source'
              : detachTextContext
                ? 'Detach Text Source'
                : 'Delete Agent'
        }
        content={
          detachFileContext
            ? `Remove "${detachFileContext.fileName}" from this agent? The file will still be available for other agents.`
            : detachQAContext
              ? `Remove "${detachQAContext.qaTitle}" from this agent?`
              : detachTextContext
                ? `Remove "${detachTextContext.textTitle}" from this agent?`
                : 'Are you sure you want to delete this agent? This action cannot be undone.'
        }
        confirmText={
          detachFileContext || detachQAContext || detachTextContext ? 'Detach' : 'Delete'
        }
        severity={detachFileContext || detachQAContext || detachTextContext ? 'warning' : 'error'}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmOpen(false);
          setDetachFileContext(null);
          setDetachQAContext(null);
          setDetachTextContext(null);
        }}
      />
    </Box>
  );
};

export default Agents;
